import React from 'react';
import type { UseFormRegister, FieldError, UseFormSetValue, UseFormClearErrors, UseFormSetError } from 'react-hook-form';
import { TdsField, TdsSelect, TdsCheck } from '@trv-tds/react';
import { NUMERIC_ONLY_REGEX, ALPHANUMERIC_REGEX } from '../../../constants/validationConstants.js';
import type { ValidationRules } from '../../../config/providerSearchConfig.js';

interface ValidatedFieldProps {
  name: string;
  type: 'text' | 'textarea' | 'select' | 'number' | 'date' | 'email' | 'radio' | 'multiselect' | 'textselect' | 'multiselecttag' | 'datePicker';
  label: string;
  placeholder?: string;
  disabled?: boolean;
  customClassName?: string;
  register: UseFormRegister<any>;
  setError?: UseFormSetError<any>;
  clearErrors?: UseFormClearErrors<any>;
  error?: FieldError | undefined;
  options: Array<{ id: string; value: string; group?: string }>;
  setValue?: UseFormSetValue<any>;
  colSize: string;
  colSizeSm?: string;
  colSizeMd?: string;
  colSizeLg?: string;
  value: string | string[];
  onChangeCustom?: (e: any) => void;
  validation?: ValidationRules | undefined;
  limitations?: {
    pattern?: RegExp;
    maxLength?: number;
  } | undefined;
  formValues?: Record<string, any>;
  isFormSubmitted?: boolean;
  trigger?: (names?: string | string[]) => Promise<boolean>;
  required?: boolean;
  large?: boolean;
  contentAlign?: 'center' | 'left' | 'right';
  radioContent?: React.ReactNode;
  radioLabel?: React.ReactNode;
  maxSelection?: number;
  helpText?: string;
  filter?: boolean;
  groupBy?: string;
}

const ValidatedField: React.FC<ValidatedFieldProps> = ({
  name,
  type,
  label,
  placeholder,
  disabled = false,
  customClassName = '',
  register,
  error,
  options,
  setValue,
  colSize,
  colSizeSm,
  colSizeMd,
  colSizeLg,
  value,
  onChangeCustom,
  validation,
  limitations,
  formValues,
  isFormSubmitted,
  trigger,
  maxSelection,
  helpText,
  filter,
  groupBy,
}) => {

  // Handle validation triggering for cross-field validation
  const handleValidationTrigger = () => {
    // Only proceed with validation if form has been submitted
    if (!isFormSubmitted || !trigger || !validation || !validation.requiredEitherOr && !validation.conditionalGroup) {
      return;
    }

    setTimeout(() => {
      // Always trigger validation for the current field (for pattern, length, required, etc.)
      const fieldsToTrigger = [name];

      // Handle requiredEitherOr validation - now contains all fields in the group
      if (validation.requiredEitherOr && validation.requiredEitherOr.fields) {
        validation.requiredEitherOr.fields.forEach(fieldName => {
          if (typeof fieldName === 'string' && fieldName !== name) {
            fieldsToTrigger.push(fieldName);
          }
        });
      }

      // Handle conditionalGroup validation
      if (validation.conditionalGroup && validation.conditionalGroup.fields) {
        const fields = validation.conditionalGroup.fields;
        if (Array.isArray(fields) && Array.isArray(fields[0]) && typeof fields[1] === 'string') {
          const andGroup = fields[0];
          const orField = fields[1];

          // Add all related fields
          andGroup.forEach(fieldName => {
            if (fieldName && fieldName !== name) {
              fieldsToTrigger.push(fieldName);
            }
          });
          if (orField && orField !== name) {
            fieldsToTrigger.push(orField);
          }
        }
      }

      // Trigger validation for all fields at once (remove duplicates)
      const uniqueFields = Array.from(new Set(fieldsToTrigger));
      trigger(uniqueFields);
    }, 0);
  };

  // Phone masking helpers for US format XXX-XXX-XXXX
  // Phone masking helpers for US format (XXX)-XXX-XXXX
  const formatPhoneWithParens = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
    return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };
  const isPhoneField = name.toLowerCase().includes('phone');
  // Build complete validation rules
  const getValidationRules = () => {
    if (!validation) return {};

    const rules: any = {};

    // Helper to create rule with default message
    const createRule = (value: any, defaultMsg: string, customMsg?: string) =>
      typeof value === 'object' && 'value' in value
        ? { value: value.value, message: value.message || customMsg || defaultMsg }
        : { value, message: customMsg || defaultMsg };

    Object.keys(validation).forEach(key => {
      const val = validation[key as keyof typeof validation];

      switch (key) {
        case 'required':
          rules.required = typeof val === 'boolean'
            ? (val ? `${label} is required` : false)
            : createRule(val, `${label} is required`);
          break;
        case 'requiredEitherOr':
          if (val && typeof val === 'object' && 'fields' in val) {
            rules.validate = {
              requiredEitherOr: (_currentValue: string) => {
                if (!formValues) return true;
                const { fields, message } = val;

                // Check if any field in the fields array has a value
                const hasAnyFieldValue = fields.some(fieldName => {
                  return typeof fieldName === 'string' && formValues[fieldName] && formValues[fieldName].toString().trim() !== '';
                });

                if (hasAnyFieldValue) {
                  return true;
                }
                return message || `Either ${label} or one of the related fields is required`;
              }
            };
          }
          break;
        case 'conditionalGroup':
          // Generic validation for [[andField1, andField2], orField3] conditional logic
          if (val && typeof val === 'object' && 'fields' in val) {
            if (!rules.validate) rules.validate = {};
            rules.validate.conditionalGroup = (_currentValue: string) => {
              if (!formValues) return true;
              const { fields, message } = val;
              let andGroup: string[] = [];
              let orField = '';
              let andField1 = '', andField2 = '', orFieldValue = '';
              if (Array.isArray(fields) && Array.isArray(fields[0]) && typeof fields[1] === 'string') {
                andGroup = fields[0];
                orField = fields[1];
                andField1 = andGroup[0] ? formValues[andGroup[0]]?.trim() : '';
                andField2 = andGroup[1] ? formValues[andGroup[1]]?.trim() : '';
                orFieldValue = formValues[orField]?.trim();
              }

              // Use single string message (simplified from array)
              const errorMessage = message || `Enter both ${andGroup[0]} and ${andGroup[1]} or ${orField} to search`;

              // If orField has value, all errors removed
              if (orFieldValue) return true;

              // If all empty, show error on all
              if (!andField1 && !andField2 && !orFieldValue) {
                return errorMessage;
              }

              // If only one of andGroup has value, show error only on the other
              if (andGroup.length > 0 && name === andGroup[0]) {
                if (andField1) return true;
                if (andField2) return errorMessage;
              }
              if (andGroup.length > 1 && name === andGroup[1]) {
                if (andField2) return true;
                if (andField1) return errorMessage;
              }
              // orField should not show error if andGroup is partially filled
              if (name === orField) {
                return true;
              }

              // If both andGroup fields filled, valid
              if (andField1 && andField2) {
                return true;
              }
              return true;
            };
          }
          break;
        case 'pattern':
          rules.pattern = val instanceof RegExp
            ? createRule(val, `${label} format is invalid`)
            : createRule(val, `${label} format is invalid`);
          break;
        case 'maxLength':
          {
            const maxVal = typeof val === 'number' ? val : (val as any).value;
            rules.maxLength = createRule(val, `${label} must be ${maxVal} characters or less`);
            break;
          }
        case 'minLength':
          {
            const minVal = typeof val === 'number' ? val : (val as any).value;
            rules.minLength = createRule(val, `${label} must be at least ${minVal} characters`);
            break;
          }
        default:
          rules[key] = val;
      }
    });

    return rules;
  };

  // Build responsive column classes
  const getResponsiveColumnClass = () => {
    const classes = [`tds-col-${colSize}`];

    if (colSizeSm) classes.push(`tds-col-sm-${colSizeSm}`);
    if (colSizeMd) classes.push(`tds-col-md-${colSizeMd}`);
    if (colSizeLg) classes.push(`tds-col-lg-${colSizeLg}`);
    if (customClassName) classes.push(customClassName);

    return classes.join(' ');
  };


  // Handle key press for limitations (pattern and maxLength restrictions)
  const handledKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isPhoneField) {
      const key = e.key;
      if (e.ctrlKey || e.metaKey || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', 'Escape'].includes(key)) return;
      if (key === '-' || !/\d/.test(key)) { e.preventDefault(); return; }
      const digits = e.currentTarget.value.replace(/\D/g, '');
      if (digits.length >= 10) { e.preventDefault(); return; }
      return;
    }

    // Prevent Input from entering invalid pattern or Regex values
    if (limitations) {
      const { pattern, maxLength } = limitations;
      const currentValue = e.currentTarget.value;
      const key = e.key;

      const selectionStart = e.currentTarget.selectionStart;
      const selectionEnd = e.currentTarget.selectionEnd;
      const selectedLength =
        selectionStart !== null && selectionEnd !== null
          ? Math.max(0, selectionEnd - selectionStart)
          : 0;

      // Allow navigation and control keys (including copy/paste operations)
      if (e.ctrlKey || e.metaKey || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter', 'Escape'].includes(key)) {
        return;
      }

      // Check maxLength restriction
      // Allow replacement when text is selected (e.g., select-all then type)
      if (maxLength) {
        const projectedLength = currentValue.length - selectedLength + key.length;
        if (projectedLength > maxLength) {
          e.preventDefault();
          return;
        }
      }

      // Check pattern restriction - convert string patterns to character-level tests
      if (pattern) {
        let charPattern = pattern;

        // Map common string patterns to character patterns
        if (pattern.source === NUMERIC_ONLY_REGEX.source) {
          charPattern = NUMERIC_ONLY_REGEX;
        } else if (pattern.source === ALPHANUMERIC_REGEX.source) {
          charPattern = ALPHANUMERIC_REGEX;
        }

        if (!charPattern.test(key)) {
          e.preventDefault();
          return;
        }
      }
    }
  };


  // Handle paste event for textbox inputs
  const handlePasteEvent = (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Prevent paste if limitations are set for textbox
    if (limitations) {
      const { pattern, maxLength } = limitations;
      const currentValue = e.currentTarget.value;
      const selectionStart = e.currentTarget.selectionStart;
      const selectionEnd = e.currentTarget.selectionEnd;
      const selectedLength =
        selectionStart !== null && selectionEnd !== null
          ? Math.max(0, selectionEnd - selectionStart)
          : 0;
      const pastedText = e.clipboardData.getData('text');

      if (maxLength) {
        const projectedLength = currentValue.length - selectedLength + pastedText.length;
        if (projectedLength > maxLength) {
          e.preventDefault();
          return;
        }
      }

      if (pattern && !pattern.test(pastedText)) {
        e.preventDefault();
      }
    }
  };

  //  Change event handler for textbox inputs
  const onChangeEventTextbox = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isPhoneField) {
      const formatted = formatPhoneWithParens(e.target.value);
      // Debug: log the value and regex test result
      if (setValue) setValue(name, formatted);
      if (onChangeCustom) {
        const syntheticEvent = { ...e, target: { ...e.target, value: formatted } };
        onChangeCustom(syntheticEvent);
      }
      handleValidationTrigger();
      return;
    }

    // Handle validation triggering
    handleValidationTrigger();

    // Call custom onChange if provided from parent component
    if (onChangeCustom) {
      onChangeCustom(e);
    }
  }


  // Change event handler for select inputs
  const onChangeEventSelect = (e: any) => {
    // Handle validation triggering
    handleValidationTrigger();

    // Call custom onChange if provided from parent component
    if (onChangeCustom) {
      onChangeCustom(e);
    }
  };

  // Render input based on type
  const renderInput = () => {
    const finalValidationRules = getValidationRules();

    switch (type) {
      case 'text':
        return (
          <input
            type="text"
            {...register(name, {
              ...finalValidationRules,
            })}
            className="tds-field__input"
            id={`${name}-input`}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => { onChangeEventTextbox(e) }}
            onKeyDown={(e) => { handledKeyDown(e) }}
            onPaste={(e) => { handlePasteEvent(e) }}
          />
        );

      case 'textarea':
        return (
          <textarea
            {...register(name, {
              ...finalValidationRules,
            })}
            className="tds-field__input"
            id={`${name}-textarea`}
            disabled={disabled}
            placeholder={placeholder}
            rows={3}
            onChange={(e) => { onChangeEventTextbox(e) }}
            onKeyDown={(e) => { handledKeyDown(e) }}
            onPaste={(e) => { handlePasteEvent(e) }}
          />
        );
      case 'datePicker':
        return (
          <input
            {...register(name, {
              ...finalValidationRules,
            })}
            type="date"
            className="tds-field__input"
            id={`${name}-input`}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => { onChangeEventTextbox(e) }}
            onKeyDown={(e) => { handledKeyDown(e) }}
            onPaste={(e) => { handlePasteEvent(e) }}
          />
        );

      case 'number':
        return (
          <input
            {...register(name, {
              ...finalValidationRules,
            })}
            type="text"
            className="tds-field__input"
            id={`${name}-input`}
            disabled={disabled}
            placeholder={placeholder}
            onChange={(e) => { onChangeEventTextbox(e) }}
            onKeyDown={(e) => { handledKeyDown(e) }}
            onPaste={(e) => { handlePasteEvent(e) }}
          />
        );

      case 'select':
        return (
          <TdsSelect
            {...register(name, finalValidationRules)}
            options={options}
            id={`${name}-select`}
            disabled={disabled}
            optionName="value"
            optionValue="id"
            value={value as string}
            placeholder={placeholder || `Select ${label}`}
            onTdsChange={(e) => { onChangeEventSelect(e) }}
            {...(groupBy ? { groupBy } : {})}
          />
        );

      case 'textselect':
        return (
          <TdsSelect
            {...register(name, finalValidationRules)}
            options={options}
            id={`${name}-textselect`}
            disabled={disabled}
            optionName="value"
            optionValue="id"
            value={value as string}
            {...(placeholder && { placeholder })}
            onTdsChange={(e) => { onChangeEventSelect(e) }}
            allowOther="true"
            filter-min-length="2"
          />
        );

      case 'multiselecttag':
        {
          // Convert string value to array if needed
          let tagSelectValues: string[] = [];

          if (typeof value === 'string') {
            if (value.includes(',')) {
              tagSelectValues = value.split(',').map(v => v.trim()).filter(v => v !== '');
            } else if (value.trim() !== '') {
              tagSelectValues = [value.trim()];
            }
          } else if (Array.isArray(value)) {
            tagSelectValues = value.map(v => String(v).trim());
          }

          return (
            <TdsSelect
              ref={register(name, finalValidationRules).ref}
              name={name}
              options={options}
              id={`${name}-multiselecttag`}
              disabled={disabled}
              optionName="value"
              optionValue="id"
              values={tagSelectValues as any}
              {...(placeholder && { placeholder })}
              {...(maxSelection && { maxSelection })}
              multiple={true}
              tags="inside"
              clear={true}
              {...(filter !== undefined && { filter })}
              {...(groupBy ? { groupBy } : {})}
              onTdsChange={(e) => {

                // Get values from the event detail
                const newValues = e.detail.values || e.detail.value || [];

                // Ensure we have an array
                const valueArray = Array.isArray(newValues) ? newValues : [newValues].filter(Boolean);
                // Convert to comma-separated string for form state
                const stringValue = valueArray.join(',');

                // Update the form with the string value
                if (setValue) {
                  setValue(name, stringValue);
                }

                // Handle validation triggering
                handleValidationTrigger();

                // Call custom onChange if provided from parent component
                if (onChangeCustom) {
                  onChangeCustom(e);
                }
              }}
              onBlur={register(name, finalValidationRules).onBlur}
            />
          );
        }

      case 'multiselect':
        {
          // Convert string value to array if neede

          // Improved conversion logic that handles single values correctly
          let multiSelectValues: string[] = [];

          if (typeof value === 'string') {
            // If it's a comma-separated string, split it
            if (value.includes(',')) {
              multiSelectValues = value.split(',').filter(v => v.trim() !== '');
            }
            // If it's a single value string, make it a one-item array
            else if (value.trim() !== '') {
              multiSelectValues = [value];
            }
          }
          // If it's already an array, use it directly
          else if (Array.isArray(value)) {
            multiSelectValues = value;
          }

          return (
            <TdsSelect
              ref={register(name, finalValidationRules).ref}
              name={name}
              options={options}
              id={`${name}-multiselect`}
              disabled={disabled}
              optionName="value"
              optionValue="id"
              values={multiSelectValues as any}
              placeholder={placeholder || `Select ${label}`}
              filter={filter === true}
              {...(groupBy ? { groupBy } : {})}
              // Align behavior with multiselecttag for grouping support
              clear={true}
              multiple={true}
              onTdsChange={(e) => {

                // Get values from the event detail
                // TdsSelect might provide values in different ways
                const newValues = e.detail.values || e.detail.value || [];

                // Ensure we have an array
                const valueArray = Array.isArray(newValues) ? newValues : [newValues].filter(Boolean);

                // Convert to comma-separated string for form state
                const stringValue = valueArray.join(',');


                // Update the form with the string value
                if (setValue) {
                  setValue(name, stringValue);
                }

                // Handle validation triggering
                handleValidationTrigger();

                // Call custom onChange if provided from parent component
                if (onChangeCustom) {
                  onChangeCustom(e);
                }
              }}
              onBlur={register(name, finalValidationRules).onBlur}
            />
          );
        }
      case 'radio':
        {
          // Register the field once at the group level
          const registration = register(name, finalValidationRules);

          return (
            <div className="radio-group tds-d-flex tds-flex-row tds-align-items-baseline tds-gap-md" ref={registration.ref}>
              {options.map((option) => {
                // Check if this option should be selected
                // Compare the current value with this option's id
                const isChecked = value === option.id;

                return (
                  <TdsCheck
                    key={option.id}
                    type="radio"
                    name={name}
                    value={option.id}
                    checked={isChecked} // Set checked based on comparison
                    disabled={disabled}
                    //large={large}
                    contentAlign="left"
                    onTdsChange={(e: any) => {
                      // Manually call the onChange from registration
                      registration.onChange(e);

                      if (setValue) {
                        setValue(name, e.target.value);
                      }
                      handleValidationTrigger();
                      if (onChangeCustom) {
                        onChangeCustom(e);
                      }
                    }}
                    onBlur={registration.onBlur}
                  >
                    {option.value}
                  </TdsCheck>
                );
              })}
            </div>
          );
        }




      default:
        return (
          <input
            {...register(name, finalValidationRules)}
            type="text"
            className="tds-field__input"
            id={`${name}-input`}
            disabled={disabled}
            placeholder={placeholder}
          />
        );
    }
  };

  // Calculate dynamic help text for multiselecttag
  const getDynamicHelpText = () => {
    if (type === 'multiselecttag') {
      let selectedCount = 0;
      if (typeof value === 'string' && value.trim() !== '') {
        selectedCount = value.includes(',') ? value.split(',').filter(v => v.trim() !== '').length : 1;
      } else if (Array.isArray(value)) {
        selectedCount = value.length;
      }
      const maxCount = maxSelection || 8;

      // Show static helpText when nothing is selected, otherwise show count
      if (selectedCount === 0) {
        return helpText || `Select up to ${maxCount}`;
      }
      return `${selectedCount} of ${maxCount} selected`;
    }
    return helpText;
  };

  const displayHelpText = getDynamicHelpText();

  // For radio, render label and options inline on a single row
  if (type === 'radio') {
    return (
      <div className={`${getResponsiveColumnClass()} tds-d-flex tds-flex-row tds-align-items-baseline`}>
        <label className="tds-field__label tds-m-0 tds-mr-xl" htmlFor={`${name}-radio-group`}>{label}</label>
        {renderInput()}
      </div>
    );
  }

  return (
    <div className={`${getResponsiveColumnClass()} tds-field`}>
      {error ? (
        <TdsField
          label={label}
          alert="error"
          message={error.message || ''}
          message-aria-live="assertive"
          {...(type === 'multiselecttag' && name === 'serviceCodes' && displayHelpText && { helpText: displayHelpText })}
        >
          {renderInput()}
        </TdsField>
      ) : (
        <TdsField label={label} {...(type === 'multiselecttag' && name === 'serviceCodes' && displayHelpText && { helpText: displayHelpText })}>
          {renderInput()}
        </TdsField>
      )}
    </div>
  );
};

export default ValidatedField;